// src/features/users/components/UsersList.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  PencilIcon,
  UserIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import EmptyState from "../../../components/UI/EmptyState";
import LoadingSpinner from "../../../components/UI/LoadingSpinner";
import ErrorMessage from "../../../components/UI/ErrorMessage";
import { profilesService } from "../../../api/profiles";
import { useAuth } from '../../../hooks/useAuth';

const UsersList = ({
  users,
  loading,
  error,
  onEdit,
  changeRoleProfile,
  onValorHoraEdit,
  onDelete,
  className = "",
}) => {

  const { isManager } = useAuth();

  // 🔥 Estados para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [mobileItemsPerPage, setMobileItemsPerPage] = useState(5);

  // 🔥 Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [sortField, setSortField] = useState("username");
  const [sortDirection, setSortDirection] = useState("asc");

  // 🔥 Determinar cuántos items mostrar según el tamaño de pantalla
  const getEffectiveItemsPerPage = () => {
    return window.innerWidth < 768 ? mobileItemsPerPage : itemsPerPage;
  };

  // 🔥 Función para obtener información visual del rol
  const getRoleDisplay = (role) => {
    const roles = {
      professional: {
        label: "Profesional",
        color: "bg-blue-100 text-blue-800",
        icon: "👨‍💼",
      },
      supervisor: {
        label: "Supervisor",
        color: "bg-green-100 text-green-800",
        icon: "👔",
      },
      manager: {
        label: "Gerente",
        color: "bg-purple-100 text-purple-800",
        icon: "👨‍💼",
      },
    };
    return (
      roles[role] || {
        label: role,
        color: "bg-gray-100 text-gray-800",
        icon: "👤",
      }
    );
  };

  // 🔥 Filtrar y ordenar usuarios
  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];

    let filtered = [...users];

    // Aplicar filtro de búsqueda
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (user.username && user.username.toLowerCase().includes(term)) ||
          (user.email && user.email.toLowerCase().includes(term)) ||
          (user.full_name && user.full_name.toLowerCase().includes(term)) ||
          (user.first_name && user.first_name.toLowerCase().includes(term)) ||
          (user.last_name && user.last_name.toLowerCase().includes(term)) ||
          (user.phone_number && user.phone_number.includes(term)),
      );
    }

    // Aplicar filtro de rol
    if (roleFilter !== "todos") {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    // Aplicar filtro de estado
    if (statusFilter !== "todos") {
      filtered = filtered.filter((user) =>
        statusFilter === "activos" ? user.is_active : !user.is_active,
      );
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";

      // Manejar valores nulos/undefined
      aValue =
        aValue === null || aValue === undefined
          ? ""
          : String(aValue).toLowerCase();
      bValue =
        bValue === null || bValue === undefined
          ? ""
          : String(bValue).toLowerCase();

      if (sortDirection === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filtered;
  }, [users, searchTerm, roleFilter, statusFilter, sortField, sortDirection]);

  // 🔥 Calcular datos de paginación
  const totalItems = filteredUsers.length;
  const effectiveItemsPerPage = getEffectiveItemsPerPage();
  const totalPages = Math.ceil(totalItems / effectiveItemsPerPage);

  // 🔥 Calcular índices para la página actual
  const startIndex = (currentPage - 1) * effectiveItemsPerPage;
  const endIndex = Math.min(startIndex + effectiveItemsPerPage, totalItems);
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // 🔥 Resetear a página 1 cuando cambian los datos o filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filteredUsers.length,
    itemsPerPage,
    mobileItemsPerPage,
    searchTerm,
    roleFilter,
    statusFilter,
  ]);

  // 🔥 Efecto para detectar cambios en el tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth < 768 &&
        currentPage > Math.ceil(totalItems / mobileItemsPerPage)
      ) {
        setCurrentPage(1);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentPage, totalItems, mobileItemsPerPage]);

  // 🔥 Navegación de páginas
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // 🔥 Manejo de eliminación
  const handleDelete = async (user) => {
    if (
      window.confirm(
        `¿Estás seguro de que quieres eliminar al usuario "${user.username}"?\n\nEsta acción no se puede deshacer.`,
      )
    ) {
      try {
        await onDelete(user.id);
        // Si eliminamos el último item de la página y no es la página 1, retroceder
        if (currentUsers.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        console.error("Error eliminando usuario:", error);
      }
    }
  };

  // 🔥 Manejar cambio de rol
  const handleRoleChange = async (userId, newRole) => {
    const opciones = isManager 
      ? ['supervisor', 'professional', 'manager']
      : ['supervisor', 'professional'];

    if(!opciones.includes(newRole.toLowerCase())) {
      alert(`❌ Rol inválido. Opciones permitidas: ${opciones.join(', ')}`);
      return;
    }
    if (!isManager && newRole === 'manager'){
      window.alert("No puede cambia a un rol superior")
      return;
    }
    if (
      window.confirm(
        "¿Estás seguro de que quieres cambiar el rol de este usuario?",
      )
    ) {
      try {
        await profilesService.changeRoleProfile(userId, newRole);
      } catch (error) {
        console.error("Error cambiando rol:", error);
      }
    }
  };

  // 🔥 Ordenar por campo
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // 🔥 Limpiar filtros
  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("todos");
    setStatusFilter("todos");
    setSortField("username");
    setSortDirection("asc");
  };

  // 🔥 Componente de paginación para desktop
  const DesktopPagination = () => (
    <div className="hidden md:flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={goToPrevPage}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          Anterior
        </button>
        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          Siguiente
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{startIndex + 1}</span> a{" "}
            <span className="font-medium">{endIndex}</span> de{" "}
            <span className="font-medium">{totalItems}</span> usuarios
            {(searchTerm ||
              roleFilter !== "todos" ||
              statusFilter !== "todos") && (
              <span className="text-gray-500 ml-2">(filtrados)</span>
            )}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {/* Selector de items por página */}
          <div className="flex items-center">
            <label
              htmlFor="items-per-page"
              className="text-sm text-gray-700 mr-2"
            >
              Mostrar:
            </label>
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="block w-20 py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Navegación de páginas */}
          <nav
            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            aria-label="Pagination"
          >
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="sr-only">Primera página</span>
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            </button>

            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="sr-only">Anterior</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {/* Números de página */}
            {(() => {
              const pageNumbers = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(
                1,
                currentPage - Math.floor(maxVisiblePages / 2),
              );
              let endPage = Math.min(
                totalPages,
                startPage + maxVisiblePages - 1,
              );

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
              }

              return pageNumbers.map((number) => (
                <button
                  key={number}
                  onClick={() => goToPage(number)}
                  className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                    currentPage === number
                      ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {number}
                </button>
              ));
            })()}

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="sr-only">Siguiente</span>
              <ChevronRightIcon className="h-4 w-4" />
            </button>

            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="sr-only">Última página</span>
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          </nav>

          {/* Input para saltar a página específica */}
          <div className="flex items-center ml-4">
            <span className="text-sm text-gray-700 mr-2">Ir a:</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  goToPage(page);
                }
              }}
              className="w-16 py-1 px-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <span className="text-sm text-gray-700 ml-2">de {totalPages}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // 🔥 Componente de paginación para móvil
  const MobilePagination = () => (
    <div className="md:hidden bg-white border-t border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 flex justify-between">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            Anterior
          </button>

          <div className="flex items-center">
            <span className="text-sm text-gray-700 mx-3">
              Página {currentPage} de {totalPages}
            </span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Siguiente
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Mostrando {startIndex + 1}-{endIndex} de {totalItems}
        </div>

        <div className="flex items-center">
          <label
            htmlFor="mobile-items-per-page"
            className="text-sm text-gray-700 mr-2"
          >
            Por página:
          </label>
          <select
            id="mobile-items-per-page"
            value={mobileItemsPerPage}
            onChange={(e) => setMobileItemsPerPage(Number(e.target.value))}
            className="block py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            {[3, 5, 10].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  // 🔥 Barra de filtros y búsqueda
  const FilterBar = () => (
    <div className="bg-white p-4 border-b border-gray-200">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Búsqueda */}
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar usuarios..."
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Filtro de Rol */}
          <div className="w-full sm:w-auto">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="block w-full sm:w-auto py-2 pl-3 pr-10 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="todos">Todos los roles</option>
              <option value="profesional">Profesional</option>
              <option value="supervisor">Supervisor</option>
              <option value="manager">Gerente</option>
            </select>
          </div>

          {/* Filtro de Estado */}
          <div className="w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full sm:w-auto py-2 pl-3 pr-10 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="todos">Todos los estados</option>
              <option value="activos">Solo activos</option>
              <option value="inactivos">Solo inactivos</option>
            </select>
          </div>

          {/* Botón limpiar filtros */}
          {(searchTerm ||
            roleFilter !== "todos" ||
            statusFilter !== "todos") && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Info de filtros activos */}
      {(searchTerm || roleFilter !== "todos" || statusFilter !== "todos") && (
        <div className="mt-3 flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Buscando: "{searchTerm}"
            </span>
          )}
          {roleFilter !== "todos" && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Rol: {getRoleDisplay(roleFilter).label}
            </span>
          )}
          {statusFilter !== "todos" && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Estado: {statusFilter === "activos" ? "Activos" : "Inactivos"}
            </span>
          )}
        </div>
      )}
    </div>
  );

  // 🔥 Indicadores de ordenamiento
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-xs">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // 🔥 Estado de carga
  if (loading) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
      >
        <FilterBar />
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner text="Cargando usuarios..." />
        </div>
      </div>
    );
  }

  // 🔥 Estado de error
  if (error) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
      >
        <FilterBar />
        <div className="p-4">
          <ErrorMessage
            error={error}
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  // 🔥 Estado vacío
  if (!users?.length) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
      >
        <FilterBar />
        <EmptyState
          icon={UserIcon}
          title="No hay usuarios"
          description="No hay usuarios registrados en el sistema."
          actionText="Crear primer usuario"
          onAction={() => {
            /* Implementar creación */
          }}
        />
      </div>
    );
  }

  // 🔥 Sin resultados de filtro
  if (filteredUsers.length === 0) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
      >
        <FilterBar />
        <EmptyState
          icon={FunnelIcon}
          title="No hay resultados"
          description="No se encontraron usuarios con los filtros aplicados."
          actionText="Limpiar filtros"
          onAction={clearFilters}
        />
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
    >
      {/* Contador de resultados para mobile */}
      <div className="md:hidden bg-gray-50 px-4 py-2 border-b border-gray-200">
        <p className="text-sm text-gray-600">
          Mostrando {startIndex + 1}-{endIndex} de {totalItems} usuarios
          {(searchTerm || roleFilter !== "todos" || statusFilter !== "todos") &&
            " (filtrados)"}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("username")}
              >
                Usuario <SortIndicator field="username" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("full_name")}
              >
                Nombre <SortIndicator field="full_name" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Rol
              </th>
              {isManager &&
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("valor_hora")}
              >
                Valor/Hora <SortIndicator field="valor_hora" />
              </th>
              }
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Estado
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Último Acceso
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentUsers.map((user) => {
              const roleInfo = getRoleDisplay(user.role);
              return (
                <tr
                  key={user.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.username}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {user.email}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          ID: {user.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">
                      {user.full_name ||
                        `${user.first_name || ""} ${user.last_name || ""}`.trim()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {user.phone_number || "Sin teléfono"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          const newRole = prompt(
                            `Cambiar rol de ${user.username}\n\nOpciones: 
                             ${isManager ? 'supervisor, profesional, manager' :
                                'supervisor, profesional'
                              }`,
                            user.role,
                          );
                          if (newRole && newRole !== user.role) {
                            handleRoleChange(user.id, newRole);
                          }
                        }}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color} hover:opacity-80 transition-opacity cursor-pointer`}
                        title="Haz clic para cambiar el rol"
                      >
                        <span className="mr-1">{roleInfo.icon}</span>
                        {roleInfo.label}
                      </button>
                    </div>
                  </td>
                  {isManager &&
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => onValorHoraEdit(user)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      title="Editar valor hora"
                    >
                      <span className="font-bold">
                        $
                        {user.valor_hora
                          ? user.valor_hora.toLocaleString()
                          : "0"}
                      </span>
                      <span className="ml-1 text-xs text-blue-600">/hora</span>
                    </button>
                  </td>
                  }
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.is_active ? (
                        <>
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                          <span className="text-sm font-medium text-green-700">
                            Activo
                          </span>
                        </>
                      ) : (
                        <>
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1" />
                          <span className="text-sm font-medium text-red-700">
                            Inactivo
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString("es-CO")
                        : "Nunca"}
                    </div>
                    
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(user)}
                        className="inline-flex items-center px-2 py-1 text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                        title="Editar usuario"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                        title="Eliminar usuario"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs">Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 p-4">
        {currentUsers.map((user) => {
          const roleInfo = getRoleDisplay(user.role);
          return (
            <div
              key={user.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {user.username}
                    </h3>
                    <p className="text-sm text-gray-600 truncate">
                      {user.email}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">ID: {user.id}</p>
                  </div>
                </div>
                <div className="flex space-x-1 ml-2 flex-shrink-0">
                  <button
                    onClick={() => onEdit(user)}
                    className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                    title="Editar usuario"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(user)}
                    className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                    title="Eliminar usuario"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <span className="text-gray-500 font-medium w-20 flex-shrink-0">
                    Nombre:
                  </span>
                  <span className="text-gray-900 flex-1">
                    {user.full_name ||
                      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                      "No especificado"}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="text-gray-500 font-medium w-20 flex-shrink-0">
                    Teléfono:
                  </span>
                  <span className="text-gray-900">
                    {user.phone_number || "No registrado"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Rol:</span>
                  <button
                        onClick={() => {
                          const newRole = prompt(
                            `Cambiar rol de ${user.username}\n\nOpciones: 
                             ${isManager ? 'supervisor, profesional, manager' :
                                'supervisor, profesional'
                              }`,
                            user.role,
                          );
                          if (newRole && newRole !== user.role) {
                            handleRoleChange(user.id, newRole);
                          }
                        }}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color} hover:opacity-80 transition-opacity cursor-pointer`}
                        title="Haz clic para cambiar el rol"
                      >
                        <span className="mr-1">{roleInfo.icon}</span>
                        {roleInfo.label}
                      </button>
                </div>
                {isManager &&
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Valor/Hora:</span>
                  <button
                    onClick={() => onValorHoraEdit(user)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1 rounded-md"
                  >
                    ${user.valor_hora ? user.valor_hora.toLocaleString() : "0"}
                  </button>
                </div>
                }
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Estado:</span>
                  <div className="flex items-center">
                    {user.is_active ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm font-medium text-green-700">
                          Activo
                        </span>
                      </>
                    ) : (
                      <>
                        <ExclamationCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-sm font-medium text-red-700">
                          Inactivo
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <span className="text-gray-500 font-medium w-20 flex-shrink-0">
                    Último acceso:
                  </span>
                  <span className="text-gray-900">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString("es-CO")
                      : "Nunca"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Componentes de paginación */}
      <DesktopPagination />
      <MobilePagination />
    </div>
  );
};

export default UsersList;
